import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import BackrsFilledDropShadow from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { TYPOGRAPHY_VARIANTS } from '@backrs_ui/common-ui/src/designSystem/typography/typography';

import { Box, ClickAwayListener, SxProps, TextField, Theme, Typography } from '@mui/material';

import { ProfilePopupWidget } from './ProfilePopupWidget';
import { SidebarMenu } from './sidebar.types';
import { SidebarItem } from './SidebarItem';

export const SIDEBAR_COLLAPSED_WIDTH = 72;
export const SIDEBAR_EXPANDED_WIDTH = 296;
const SIDEBAR_COLLAPSED_WIDTH_WITH_SCROLLBAR = 80;
const SIDEBAR_STRINGS = appStrings.modules.backrsSidebar;

type BackrsSidebarProps = Readonly<{
  sidebar: SidebarMenu;
}>;

export const BackrsSidebar = ({ sidebar }: BackrsSidebarProps) => {
  const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);
  const [expandedItem, setExpandedItem] = useState<string>();

  const handleSettingExpandedItem = (itemName?: string, shouldOpenSidebar?: boolean) => {
    if (shouldOpenSidebar) {
      setIsSideBarOpen(true);
    }

    setExpandedItem(itemName);
  };

  const handleToggleSidebar = () => {
    setIsSideBarOpen((prev) => !prev);
    setExpandedItem(undefined);
  };

  const handleSearchEnterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const value = (event.target as HTMLInputElement).value;

      sidebar.search.onSearch(value);

      (event.target as HTMLInputElement).value = '';

      setIsSideBarOpen(false);
    }
  };

  const unAuthUser = sidebar.user?.isUnauthenticated;

  return (
    <ClickAwayListener onClickAway={() => setIsSideBarOpen(false)}>
      <Box sx={containerSx(isSideBarOpen)}>
        <Box sx={contentContainerSx(isSideBarOpen)}>
          {/* LOGO */}
          <Box sx={logoContainerSx(isSideBarOpen)} onClick={handleToggleSidebar}>
            <Box
              component="img"
              src={BackrsFilledDropShadow}
              alt="Backrs Logo"
              sx={backrsLogoImageSx(isSideBarOpen)}
            />
            <AnimatedCollapse isOpen={isSideBarOpen} direction="horizontal" opacityFade>
              <Typography variant="display-lg-semibold">{SIDEBAR_STRINGS.backrs}</Typography>
            </AnimatedCollapse>
          </Box>

          {/* SEARCH INPUT */}
          {!unAuthUser ? (
            <TextField
              variant="outlined"
              placeholder={SIDEBAR_STRINGS.search}
              sx={searchBarSx(isSideBarOpen, sidebar.search.isPageActive)}
              onClick={isSideBarOpen ? undefined : () => sidebar.search.onSearch()}
              onKeyDown={handleSearchEnterKey}
              InputProps={{
                startAdornment: (
                  <Box sx={expandedSearchIconAdornmentSx(isSideBarOpen)}>
                    <AppIcon
                      iconName="SearchMd"
                      diameter="20px"
                      color="Colors/Background/bg-primary-solid"
                    />
                  </Box>
                ),
              }}
            />
          ) : null}

          {/* SIDE BAR ITEMS */}
          {sidebar.sections.map((section, index) => (
            <Box key={index} sx={sidebarSectionSx}>
              {section.menuItems.map((item, index) => (
                <SidebarItem
                  key={index}
                  sidebarMenuItem={item}
                  expandedItem={expandedItem}
                  setExpandedItem={handleSettingExpandedItem}
                  isSidebarOpen={isSideBarOpen}
                  setIsSideBarOpen={setIsSideBarOpen}
                />
              ))}

              {/* SECTION BOTTOM BORDER */}
              {index !== sidebar.sections.length - 1 && !unAuthUser && (
                <Box sx={sectionBottomBorderSx} />
              )}
            </Box>
          ))}
        </Box>

        {/* PROFILE WIDGET */}
        <Box sx={profileWidgetContainerSx(isSideBarOpen)}>
          <ProfilePopupWidget
            backrsUser={sidebar.user}
            isSidebarOpen={isSideBarOpen}
            topSectionItems={sidebar.accountMenuItems.topSectionItems}
            bottomSectionItems={sidebar.accountMenuItems.bottomSectionItems}
          />
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

const containerSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    height: '100dvh',
    borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    width: isSideBarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
    transition: 'all 0.3s ease-in-out',
    overflow: 'none',
    backgroundColor: backrs.colors['Beige/50'],
    paddingBottom: isSideBarOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-3xl'],

    // smaller screen height target
    '@media screen and (min-width: 768px) and (max-width: 1366px) and (max-height: 700px)': {
      width: isSideBarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH_WITH_SCROLLBAR,
    },
    // Mobile phone specific styling
    '@media screen and (max-width: 600px)': {
      width: isSideBarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
    },
  });

const profileWidgetContainerSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    paddingLeft: isSideBarOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-lg'],
    paddingRight: isSideBarOpen ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-lg'],
    transition: 'all 0.3s ease-in-out',
  });

const contentContainerSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    gap: backrs.spacing['spacing-sm'],

    paddingTop: isSideBarOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-xl'],
    paddingBottom: backrs.spacing['spacing-lg'],
    paddingLeft: isSideBarOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-lg'],
    paddingRight: isSideBarOpen ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-lg'],

    transition: 'all 0.3s ease-in-out',
  });

const logoContainerSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    gap: backrs.spacing['spacing-2xl'],
    display: 'flex',
    marginBottom: isSideBarOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-3xl'],
    transition: 'all 0.3s ease-in-out',
    minHeight: ICON_SIZES['64px'],
  });

const searchBarSx =
  (isSideBarOpen: boolean, isPageActive: boolean): SxProps<Theme> =>
  ({ backrs }) => {
    // When collapsed, default border is either black (if active) or transparent.
    const collapsedSidebarBorder = isPageActive
      ? backrs.colors['Colors/Base/Black']
      : backrs.colors['Colors/Base/Transparent'];
    const expandedSidebarBorder = backrs.colors['Gray/300'];
    const searchBarBorder = isSideBarOpen ? expandedSidebarBorder : collapsedSidebarBorder;

    return {
      mb: backrs.spacing['spacing-xl'],
      display: 'flex',
      justifyContent: 'center',
      transition: 'all 0.3s ease-in-out',
      borderRadius: backrs.radius['radius-sm'],
      width: isSideBarOpen ? '100%' : 'auto',
      minWidth: backrs.spacing['spacing-5xl'],
      minHeight: backrs.spacing['spacing-5xl'],
      maxWidth: !isSideBarOpen ? '48px' : 'unset',

      '& .MuiOutlinedInput-root': {
        maxWidth: isSideBarOpen ? SIDEBAR_EXPANDED_WIDTH : '48px',
        minWidth: '48px',
        paddingY: backrs.spacing['spacing-md'],
        paddingX: backrs.spacing['spacing-lg'],
        borderRadius: backrs.radius['radius-md'],
        border: `1px solid ${searchBarBorder}`,
        backgroundColor:
          !isSideBarOpen && isPageActive
            ? backrs.colors['Colors/Background/bg-primary']
            : 'transparent',
        transition: `max-width 0.3s ease-in-out, background-color 0.3s ease-in-out, border 0.3s ease-in-out`,
        gap: backrs.spacing['spacing-lg'],
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: '1px solid transparent !important',
        },
        cursor: isSideBarOpen ? 'default' : 'pointer',
        '&.Mui-focused': {
          ...(isSideBarOpen
            ? {
                borderColor: backrs.colors['Colors/Border/border-brand'],
              }
            : {
                boxShadow: `0 0 0 1px transparent !important`,
                border: `1px solid ${searchBarBorder} !important`,
              }),
        },
        '&:active': {
          transition: 'all 0.3s ease-in-out',
          border: `1px solid ${backrs.colors['Colors/Border/border-brand']}`,
        },
      },
      '&:hover': !isSideBarOpen
        ? {
            backgroundColor: backrs.colors['Colors/Background/bg-primary'],
            transform: 'translate(-2px, -2px)',
            boxShadow: '4px 4px 0px 0px rgba(0, 0, 0, 1)',
          }
        : {},
      // Active state for the container to cancel hover effects
      '&:active': {
        transform: 'none !important',
        boxShadow: 'none !important',
        transition: 'all 0s !important',
      },
      // Change the child border on hover if not active.
      '&:hover .MuiOutlinedInput-root':
        !isPageActive && !isSideBarOpen
          ? {
              border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
            }
          : {},
      '&:active .MuiOutlinedInput-root': {
        boxShadow: 'none !important',
        transition: 'all 0s !important',
        transform: 'none !important',
      },
      '& .MuiOutlinedInput-input': {
        color: backrs.colors['Colors/Text/text-primary'],
        padding: backrs.spacing['spacing-none'],
        fontWeight: TYPOGRAPHY_VARIANTS['text-md-regular'].fontWeight,
        fontSize: TYPOGRAPHY_VARIANTS['text-md-regular'].fontSize,
        display: 'flex',
        borderRadius: backrs.radius['radius-sm'],
        border: 'none',
        '&::placeholder': {
          color: isSideBarOpen ? backrs.colors['Colors/Text/text-placeholder'] : 'transparent',
          transition: 'all 0.3s ease-in-out',
          opacity: 1,
        },
      },
      '& input:-webkit-autofill': {
        transition: 'background-color 600000s 0s, color 600000s 0s',
      },
      '& .MuiInputLabel-outlined': {
        color: backrs.colors['Colors/Text/text-primary'],
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    };
  };

const expandedSearchIconAdornmentSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  () => ({
    minWidth: ICON_SIZES['20px'],
    minHeight: ICON_SIZES['20px'],
    display: 'flex',
    alignItems: 'center',
    cursor: isSideBarOpen ? 'default' : 'pointer',
  });

const sidebarSectionSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-sm'],
  display: 'flex',
  flexDirection: 'column',
});

const sectionBottomBorderSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  my: backrs.spacing['spacing-xs'],
  mx: backrs.spacing['spacing-md'],
});

const backrsLogoImageSx =
  (isSideBarOpen: boolean): SxProps<Theme> =>
  () => ({
    cursor: 'pointer',
    height: isSideBarOpen ? ICON_SIZES['64px'] : ICON_SIZES['48px'],
    width: isSideBarOpen ? ICON_SIZES['64px'] : ICON_SIZES['48px'],
    transition: 'height 0.3s, width 0.3s',
  });
